import { creatorbase } from '@round/api';
import { showNotification } from 'helpers';
import useDeleteProjectCampaign from '../../hooks/useDeleteProjectCampaign';
import { useAllPosts } from '@round/creatorbase-helpers/api-hooks';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';
import { getProjectDetailsPostsParams } from '../../hooks/posts/getProjectDetailsPostsParams';
import { Button } from '@round/whitelabel-ui';
import { Modal } from '@round/whitelabel-ui';
import styles from './DeleteCampaignModal.module.css';
import cn from 'classnames';

type BaseCampaign = Pick<creatorbase.Campaign, 'id' | 'project_id' | 'platform' | 'name'>;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    campaign: BaseCampaign | null;
};

const DeleteCampaignModal = ({ isOpen, onClose, campaign }: Props) => {
    const { deleteOne: deleteCampaign, isLoading } = useDeleteProjectCampaign();

    const {
        data: posts,
        isLoading: arePostsLoading,
        hasError: postsHaveError,
    } = useAllPosts(getProjectDetailsPostsParams(campaign?.id ?? 0), {
        enabled: !!campaign && isOpen,
    });
    const totalPostCountLabel = `${posts.length} post${posts.length !== 1 ? 's' : ''}`;

    // The posts query returns active and backup posts together; call out the
    // backup count so it's clear deletion takes those too.
    const backupPostCount = posts.filter((post) =>
        backupPostsStatuses.includes(post.status as creatorbase.BackupPostStatus)
    ).length;
    const activePostCount = posts.length - backupPostCount;

    const postBreakdownLabel =
        [
            activePostCount > 0 ? `${activePostCount} post${activePostCount !== 1 ? 's' : ''}` : null,
            backupPostCount > 0 ? `${backupPostCount} backup post${backupPostCount !== 1 ? 's' : ''}` : null,
        ]
            .filter(Boolean)
            .join(', ') || '0 posts';

    const handleDelete = () => {
        if (!campaign) {
            return;
        }

        deleteCampaign(campaign, {
            onSuccess: () => {
                showNotification('Campaign deleted', 'info');
                onClose();
            },
            onError: () => {
                showNotification('Could not delete campaign', 'error');
            },
        });
    };

    return (
        <Modal className={styles.modal} closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <Modal.Header>
                <Modal.Title>Delete campaign</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.body}>
                <p className={cn(styles.copy, styles.bold)}>You’re about to permanently delete the following:</p>
                <div className={styles.banner}>
                    <p className={styles.copy}>
                        <span className={styles.bold}>Campaign</span>: {campaign?.name}
                    </p>

                    <p className={styles.copy}>
                        <span className={styles.bold}>Posts</span>:{' '}
                        {postsHaveError ? 'Could not load posts. Refresh and try again.' : postBreakdownLabel}
                    </p>
                </div>

                <p className={styles.copy}>You cannot restore campaigns and posts. They are deleted permanently.</p>
            </Modal.Body>

            <Modal.Actions>
                <Button
                    appearance="critical"
                    isLoading={isLoading || arePostsLoading}
                    disabled={arePostsLoading || postsHaveError}
                    onClick={handleDelete}
                >
                    {postsHaveError ? 'Delete 1 campaign' : `Delete 1 campaign and ${totalPostCountLabel}`}
                </Button>
                <Button appearance="ghost" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteCampaignModal;
